import type { FC, PropsWithChildren, RefObject } from 'react';
import { memo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useHeaderHeight } from '../../hooks/useHeaderHeight';
import { useAppSelector } from '../../redux/hooks';
import { selectYakliTargetMessageId } from '../../redux/slices/yakli-slice';
import type { VoteRef } from '../../services/scrollSections';
import { useCurrentScrollSection, useRegisterScrollSection } from '../../services/scrollSections';
import { isMobile } from '../../utils/web';
import { useThreadIsRoot } from '../ThreadContexts/ThreadIsRootContext';
import { mkMessageDOMId } from './thread-service';
import { getStateMsg } from './thread-slice-utils';
import type { ThreadMessageChildrenInstance } from './ThreadMessageNode';

interface ThreadMessageCardProps {
  messageId: string | undefined;
  voteRef: VoteRef;
  nestedAnswersRef: RefObject<ThreadMessageChildrenInstance>;
  isYakliDisplay?: boolean;
  className?: string;
}

export const ThreadMessageCard: FC<PropsWithChildren<ThreadMessageCardProps>> = memo(
  function ThreadMessageCard(props) {
    const { children, messageId, voteRef, nestedAnswersRef, isYakliDisplay, className } = props;
    const messageRef = useRef<HTMLDivElement>(null);

    const hasSelectedChildId = useAppSelector(
      s => !!getStateMsg(s, messageId, isYakliDisplay)?.selectedChildId,
    );

    const isMob = isMobile();

    const currentScrollSection = useCurrentScrollSection();
    const isCurrentSection = currentScrollSection?.id === messageId;

    const targetedId = useAppSelector(selectYakliTargetMessageId);
    const isTargeted = targetedId === messageId;

    const msgAuthor = useAppSelector(s => getStateMsg(s, messageId, isYakliDisplay)?.author);

    const isRoot = useThreadIsRoot();

    const { height } = useHeaderHeight();
    const scrollMarginTop = height + 60;

    useRegisterScrollSection(messageRef, {
      id: messageId,
      author: msgAuthor,
      voteRef,
      nestedAnswersRef,
      messageId: messageId,
    });

    return (
      <div
        className={twMerge(
          'ThreadMessageCard relative',
          isRoot ? 'rounded-t' : 'border-t border-solid border-gray-210',
          !hasSelectedChildId && 'rounded-b',
          isMob && isCurrentSection && 'shadow-[0_0_10px_rgb(188,188,188,0.5)] z-[1]',
          isTargeted ? 'isTargetedId bg-gray-lightest' : 'isNotTargetedId bg-white',
          className,
        )}
        style={{ scrollMarginTop }}
        id={mkMessageDOMId(messageId)}
        ref={messageRef}
      >
        {children}
      </div>
    );
  },
);
